import { useCallback, useEffect } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Label } from '@antimatterhq/ui/components/label';
import { Button } from '@antimatterhq/ui/components/button';
import { InfoTooltip } from '@antimatterhq/ui/components/infoTooltip';
import { useCreateNotebook } from '@/hooks/dbui/useCreateNotebook';
import { useGetWorkspaceConfig } from '@/hooks/workspace/useGetWorkspaceConfig';
import { AddDetectionRuleSchemaValues } from '@/zod/schemas/detectionRules/AddDetectionRule';
import {
  generateTemplateNotebook,
  notebookPathPlaceholder,
  rlBatchTooltip,
  rlInlineTooltip,
  rlNotebookTooltip,
  rlStreamingTooltip,
  ruleDetectionSqlBatchTooltip,
  ruleDetectionSqlStreamingTooltip,
} from '@/strings/StringsTooltips';
import {
  FormCodeEditor,
  FormError,
  FormInput,
  FormSqlWhereInput,
} from '@/components/form/form';
import { StreamTableSchemaValues } from '@/zod/schemas/StreamTableSchema';
import { StreamTable } from '@/components/streamTable/StreamTable';
import { generateNotebookName } from '@/lib/notebook/notebookHelper';
import { FormMultiValues } from '@/components/form/multiValues/FormMultiValues';

export const RuleInputSection = () => {
  const { mutateAsync: createNotebook, isPending: isCreatingNotebook } =
    useCreateNotebook();
  const { values, handleChange, handleBlur, setFieldValue } =
    useFormikContext<AddDetectionRuleSchemaValues>();

  const { data: workspaceConfig } = useGetWorkspaceConfig();

  const onHandleInputMethodChange = (value: string | undefined) => {
    handleChange({ target: { name: 'ruleInput', value } });
  };

  const onHandleNotebookTypeChange = (value: string | undefined) => {
    handleChange({ target: { name: 'notebookType', value } });
  };

  const createStreamingTable = useCallback(() => {
    const table: StreamTableSchemaValues = {
      name: '',
      streaming: true,
      watermark: {
        eventTimeColumn: 'time',
        delayThreshold: '',
        dropDuplicates: [''],
      },
      alias: '',
      joinType: '',
      joinExpr: '',
      joinHidden: false,
      aliasRequired: false,
    };

    return table;
  }, []);

  useEffect(() => {
    // if we are in batch mode, we need to clear the streaming tables
    if (values.ruleInput === 'batch') {
      if (values.streamingTables.length > 0) {
        setFieldValue('streamingTables', []);
      }

      return;
    }

    // if we are in streaming mode, we need to create at least one streaming table
    if (values.streamingTables.length === 0) {
      setFieldValue('streamingTables', [createStreamingTable()]);
      return;
    }

    let dirty = false;
    if (!values.streamingTables[0].joinHidden) {
      values.streamingTables[0].joinHidden = true;
      dirty = true;
    }

    if (
      values.streamingTables.length > 1 &&
      values.streamingTables.some((table) => !table.aliasRequired)
    ) {
      // eslint-disable-next-line no-param-reassign
      values.streamingTables.forEach((table) => (table.aliasRequired = true));
      dirty = true;
    }

    if (
      values.streamingTables.length === 1 &&
      values.streamingTables[0].aliasRequired
    ) {
      values.streamingTables[0].aliasRequired = false;
      dirty = true;
    }

    if (dirty) {
      setFieldValue('streamingTables', values.streamingTables);
    }
  }, [values, setFieldValue, createStreamingTable]);

  const onCreateNotebook = useCallback(async () => {
    if (!workspaceConfig) return;
    await createNotebook(
      {
        path: generateNotebookName(
          `${values.name}-${values.ruleInput === 'streaming' ? 'streamRule' : 'batchRule'}`,
          'detectionRule',
        ),
        templateType:
          values.ruleInput === 'streaming' ? 'streamRule' : 'batchRule',
      },
      {
        onSuccess: async ({ data }) => {
          setFieldValue('customNotebookPath', data.notebookPath);
          if (data.url) {
            window.open(data.url, '_blank', 'noopener,noreferrer');
          }
        },
        onError: async ({ error }) => {
          console.error(error);
        },
      },
    );
  }, [
    setFieldValue,
    createNotebook,
    values.name,
    values.ruleInput,
    workspaceConfig,
  ]);

  return (
    <div>
      <ButtonGroup
        className="w-80"
        equalWidth
        onChange={onHandleInputMethodChange}
        value={values.ruleInput}
        options={[
          {
            label: 'Streaming input',
            value: 'streaming',
            info: { content: rlStreamingTooltip },
          },
          {
            label: 'Batch input',
            value: 'batch',
            info: { content: rlBatchTooltip },
          },
        ]}
      />
      <Spacer size="base" />
      <ButtonGroup
        className="w-80"
        equalWidth
        onChange={onHandleNotebookTypeChange}
        value={values.notebookType}
        options={[
          {
            label: 'Inline',
            value: 'inline',
            info: { content: rlInlineTooltip },
          },
          {
            label: 'Use custom notebook',
            value: 'custom',
            info: { content: rlNotebookTooltip },
          },
        ]}
      />

      {values.notebookType === 'custom' && (
        <div>
          <Spacer size="lg" />
          <Label htmlFor="customNotebookPath">Notebook path</Label>
          <FormInput
            name="customNotebookPath"
            value={values.customNotebookPath}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={notebookPathPlaceholder}
          />

          <Spacer size="base" />

          <InfoTooltip
            content={generateTemplateNotebook}
            trigger={
              <Button
                variant="default"
                onClick={onCreateNotebook}
                loading={isCreatingNotebook}
                disabled={isCreatingNotebook}
                leftIcon="lucide:notebook"
                data-testid="create-notebook-button"
              >
                Generate a template notebook
              </Button>
            }
          />

          <Spacer size="lg" />

          <Label>Parameters</Label>
          <FormMultiValues
            name="customNotebookParameters"
            onBlur={handleBlur}
            onChange={handleChange}
            values={values.customNotebookParameters}
            defaultValue={{ key: '', value: '' }}
          />

          <Spacer size="lg" />
        </div>
      )}

      {values.ruleInput === 'streaming' && (
        <>
          <Spacer size="lg" />
          <Label required>Source tables:</Label>
          <FieldArray name="streamingTables">
            {({ remove, push }) => (
              <>
                {values.streamingTables.map((table, index) => (
                  <div key={`streamingTables-${index}`}>
                    {index > 0 && <Spacer size="lg" />}
                    <StreamTable
                      index={index}
                      table={table}
                      remove={remove}
                      prefix="streamingTables"
                      hideRemove={values.streamingTables.length === 1}
                      hideInputType={true}
                    />
                  </div>
                ))}

                <Spacer size="lg" />

                <Button
                  variant="default"
                  onClick={() => push(createStreamingTable())}
                  data-testid="streamingTables-add-button"
                >
                  Add table
                </Button>
              </>
            )}
          </FieldArray>
        </>
      )}

      {values.ruleInput === 'streaming' && values.notebookType !== 'custom' && (
        <>
          <Spacer size="lg" />
          <Label htmlFor="streamingFilter" id="streamingFilter-label">
            Filter (Optional)
          </Label>
          <FormSqlWhereInput
            name="streamingFilter"
            placeholder={
              "A SQL WHERE expression for pre-filtering data, e.g.\ncolumn_name IN ('val1', 'val2', 'val3') AND col2 IS NOT NULL"
            }
            value={values.streamingFilter}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-labelledby="streamingFilter-label"
          />
          <FormError name="streamingFilter" />

          <Spacer size="lg" />
          <Label
            htmlFor="streamingSql"
            id="streamingSql-label"
            info={ruleDetectionSqlStreamingTooltip}
          >
            Detection SQL
          </Label>
          <FormCodeEditor
            name="streamingSql"
            placeholder={
              'E.g.\nSELECT time, col1, col2 FROM input WHERE col2 > 15'
            }
            value={values.streamingSql}
            onChange={handleChange}
            language="sql"
            onBlur={handleBlur}
            aria-labelledby="streamingSql-label"
            className="min-h-[70px]"
          />
          <FormError name="streamingSql" />
        </>
      )}

      {values.ruleInput === 'batch' && values.notebookType !== 'custom' && (
        <div>
          <Spacer size="lg" />
          <Label
            htmlFor="batchSql"
            id="batchSql-label"
            info={ruleDetectionSqlBatchTooltip}
          >
            Detection SQL
          </Label>
          <FormCodeEditor
            placeholder={
              'E.g.\nSELECT\n\ttime, activity_name, actor\nFROM http_activity\nWHERE time > current_timestamp() - INTERVAL 5 MINUTES'
            }
            name="batchSql"
            value={values.batchSql || ''}
            language="sql"
            onChange={handleChange}
            onBlur={handleBlur}
            aria-labelledby="batchSql-label"
            className="min-h-[130px]"
          />
          <FormError name="batchSql" />
        </div>
      )}
      <Spacer size="lg" />
    </div>
  );
};
